import { markRaw, ref, type Ref } from 'vue';
import { toast } from 'vue-sonner';
import http from '@/services/http';
import GeneratedPlaylistInspector from '@/components/DataTable/GeneratedPlaylistInspector.vue';
import { useInspectorStore } from '@/stores/inspectorStore';
import { useOpenRecordInInspector } from '@/composables/useOpenRecordInInspector';

/**
 * Open the playlist a player says it is playing.
 *
 * A check-in reports a NAME, never an id — a generated filename, an id-shaped
 * filename, or a stale bare name. The server applies the parser's rules to turn
 * that into a record: custom lists by the id after "--", generated playlists by
 * file_name prefix within the player's zone, bare names by exact custom-playlist
 * name match. A custom list opens in the record inspector, a generated playlist
 * in the Generated Playlist inspector, and anything unresolvable toasts.
 *
 * This lives here rather than in a component because three places open the
 * same thing from a reported name: the player card, the inspector's Current
 * Playlist row, and the playlist filename inside a PLAYLIST log line. Three
 * copies would be three chances to disagree about what a name resolves to.
 */
interface CurrentPlaylistResolution {
    kind: 'custom_playlist' | 'generated_playlist' | 'unresolved';
    location_zone_custom_playlist_id?: number;
    generated_playlist_id?: number;
    location_zone_id?: number;
    file_name?: string;
    play_date_time?: string | null;
    track_count?: number;
}

export interface OpenPlaylistOptions {
    /**
     * The section a custom list opens on. The player's Schedule tab opens a
     * file on the list's Tracks, because the file is the list's tracks.
     */
    customListSection?: string;
}

export function useOpenCurrentPlaylist(
    locationZonePlayerId: Ref<number | null>,
): {
    openPlaylist: (playlistName: string | null, options?: OpenPlaylistOptions) => Promise<void>;
    resolving: Ref<boolean>;
} {
    const { openRecord } = useOpenRecordInInspector();
    const inspectorStore = useInspectorStore();
    const resolving = ref(false);

    async function openPlaylist(playlistName: string | null, options: OpenPlaylistOptions = {}): Promise<void> {
        const name = playlistName?.trim();
        if (!name || resolving.value) return;
        resolving.value = true;
        try {
            const response = await http.get('/api/playlists/resolve', {
                params: { name, location_zone_player_id: locationZonePlayerId.value },
            });
            const resolution = response.data as CurrentPlaylistResolution;
            if (resolution.kind === 'custom_playlist' && resolution.location_zone_custom_playlist_id) {
                if (options.customListSection) {
                    openRecord('LocationZoneCustomPlaylist', resolution.location_zone_custom_playlist_id, {
                        initialSection: options.customListSection,
                    });
                } else {
                    openRecord('LocationZoneCustomPlaylist', resolution.location_zone_custom_playlist_id);
                }
                return;
            }
            if (
                resolution.kind === 'generated_playlist' &&
                resolution.generated_playlist_id &&
                resolution.location_zone_id
            ) {
                inspectorStore.open(
                    resolution.file_name ?? name,
                    markRaw(GeneratedPlaylistInspector),
                    {
                        locationZoneId: resolution.location_zone_id,
                        generatedPlaylistId: resolution.generated_playlist_id,
                        fileName: resolution.file_name ?? name,
                    },
                    { subtitle: 'Generated Playlist' },
                );
                return;
            }
            toast.info(`No playlist named “${name}” found.`);
        } catch {
            // A click that does nothing reads as a broken button, and without
            // this the rejection escapes the handler unattached.
            toast.error(`Couldn't look up “${name}”. Try again.`);
        } finally {
            resolving.value = false;
        }
    }

    return { openPlaylist, resolving };
}
